import { useEffect, useState } from 'react';

import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';

import { RegistryOrderError } from '@/services/registry-order-error';
import {
  REGISTRY_INBOUND_DISMISS_NOTE_MAX,
  type RegistryInboundRow,
} from '@/types/registry-documents';

interface Failure {
  message: string;
  /** Nothing a second attempt would change: the email is no longer open. */
  final: boolean;
}

/** What a refused dismiss means to the person, by its kind. */
export function describeDismissFailure(err: unknown): Failure {
  if (err instanceof RegistryOrderError && err.kind === 'already_resolved') {
    return { message: 'Someone already dealt with this email.', final: true };
  }
  return { message: err instanceof Error ? err.message : 'The request failed', final: false };
}

interface RegistryInboundDismissModalProps {
  opened: boolean;
  onClose: () => void;
  /** The email being dismissed. Null before the first one is chosen; kept
   * by the caller while the dialog closes, so its line does not blank. */
  row: RegistryInboundRow | null;
  /** Throws on failure; the modal stays open and shows the message. */
  onConfirm: (note: string | null) => Promise<unknown>;
}

/**
 * Set aside an email that reached no order and is about none: a
 * newsletter from the desk, a message meant for someone else. Nothing is
 * deleted: the email stays in the mailbox and in the ledger, and only
 * stops counting on the Admin badge. The note says why, for whoever reads
 * the ledger later.
 */
export function RegistryInboundDismissModal({
  opened,
  onClose,
  row,
  onConfirm,
}: Readonly<RegistryInboundDismissModalProps>) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  // Cleared on close, so reopening for another email never inherits a note.
  useEffect(() => {
    if (!opened) {
      setNote('');
      setFailure(null);
    }
  }, [opened]);

  const canSubmit = !submitting && failure?.final !== true;

  const submit = async () => {
    setSubmitting(true);
    setFailure(null);
    try {
      await onConfirm(note.trim() || null);
    } catch (err) {
      setFailure(describeDismissFailure(err));
    } finally {
      setSubmitting(false);
    }
  };

  // A dismiss in flight is not abandoned by closing the dialog: its
  // outcome, a refusal above all, must still have somewhere to be shown.
  const keep = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Modal opened={opened} onClose={keep} title="Dismiss this email">
      <Stack>
        <Text size="sm">
          {row?.subject ? `"${row.subject}": it` : 'It'} stays in the mailbox and in the audit log,
          and stops counting on the Admin badge.
        </Text>
        <Textarea
          label="Note"
          description={`Optional · ${note.length}/${REGISTRY_INBOUND_DISMISS_NOTE_MAX}`}
          autosize
          minRows={2}
          // The API refuses a longer note.
          maxLength={REGISTRY_INBOUND_DISMISS_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          data-testid="registry-inbound-dismiss-note"
        />
        {failure && (
          <Alert
            color={failure.final ? 'orange' : 'red'}
            title="The email was not dismissed"
            data-testid="registry-inbound-dismiss-failure"
          >
            {failure.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={keep} disabled={submitting}>
            Keep
          </Button>
          <Button
            color="red"
            onClick={() => void submit()}
            disabled={!canSubmit}
            loading={submitting}
            data-testid="registry-inbound-dismiss-confirm"
          >
            Dismiss
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
